import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { ChevronDownIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useContext, useRef, useState, useEffect } from 'react';
import Button from '../Button.js';
import Card from '../Card.js';
import Overlay from '../Overlay.js';
import { SideNavContext, SideNavGroupContext } from './index.js';

var _excluded = ["as", "className", "children", "data-testid", "startExpanded", "isExpanded"];
var SideNavNavigationGroup = function SideNavNavigationGroup(_ref) {
  var _children$, _children$$type, _children$2, _children$2$type, _children$3, _children$3$type;
  var _ref$as = _ref.as,
    As = _ref$as === void 0 ? 'div' : _ref$as,
    className = _ref.className,
    children = _ref.children,
    dataTestId = _ref['data-testid'],
    startExpanded = _ref.startExpanded,
    isExpanded = _ref.isExpanded,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var parentContext = useContext(SideNavContext);
  var isInitialMount = useRef(true);
  var _useState = useState(startExpanded || isExpanded || false),
    _useState2 = _slicedToArray(_useState, 2),
    isGroupExpanded = _useState2[0],
    setIsGroupExpanded = _useState2[1];
  useEffect(function () {
    if (parentContext.isMinimized) {
      setIsGroupExpanded(false);
    }
  }, [parentContext, children]);

  // warn if there is no content
  if (!Array.isArray(children) || children.length !== 2 || ((_children$ = children[0]) === null || _children$ === void 0 ? void 0 : (_children$$type = _children$.type) === null || _children$$type === void 0 ? void 0 : _children$$type.displayName) !== 'SideNav.NavigationGroupContent' && ((_children$2 = children[1]) === null || _children$2 === void 0 ? void 0 : (_children$2$type = _children$2.type) === null || _children$2$type === void 0 ? void 0 : _children$2$type.displayName) !== 'SideNav.NavigationGroupContent') {
    console.error('Hey Canvas User! Navigation Group requires NavigationGroupContent and a header');
  }
  useEffect(function () {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Runs after component has mounted and initialized & allows for override of internal state by passed in prop
      setIsGroupExpanded(isExpanded);
    }
  }, [isExpanded]);
  var finalClasses = classnames('C-SideNav__NavigationGroup', className);
  var finalButtonClasses = classnames('--embeddedInComponent', 'C-SidNav__ToggleButton', {
    isExpanded: isGroupExpanded
  });
  var finalContentClasses = classnames('C-SideNav__NavigationGroup__Content', {
    '--disableAnimation': isInitialMount.current && startExpanded
  });
  var toggleClick = function toggleClick(event) {
    event.stopPropagation();
    event.preventDefault();
    setIsGroupExpanded(!isGroupExpanded);
  };
  var headerClick = function headerClick() {
    setIsGroupExpanded(!isGroupExpanded);
  };
  var groupContent;
  var groupHeader;
  if (((_children$3 = children[0]) === null || _children$3 === void 0 ? void 0 : (_children$3$type = _children$3.type) === null || _children$3$type === void 0 ? void 0 : _children$3$type.displayName) === 'SideNav.NavigationGroupContent') {
    groupContent = children[0];
    groupHeader = children[1];
  } else {
    groupContent = children[1];
    groupHeader = children[0];
  }
  groupContent = /*#__PURE__*/React__default.cloneElement(groupContent, {
    'aria-expanded': isGroupExpanded
  });
  var isGroupHeaderLink = groupHeader.props.to || groupHeader.props.href;
  groupHeader = /*#__PURE__*/React__default.cloneElement(groupHeader, {
    className: classnames({
      hasLink: isGroupHeaderLink
    }),
    isGroupHeader: true,
    onClick: headerClick
  });

  // we have to eat the passed click events/routing because that goes in the dropdown in the iconsOnly menu
  var iconGroupHeader = /*#__PURE__*/React__default.cloneElement(groupHeader, {
    as: 'button',
    href: null,
    onClick: headerClick,
    tabIndex: 0,
    to: null
  });
  if (parentContext !== null && parentContext !== void 0 && parentContext.isMinimized) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "C-SideNav__ListContainer"
    }, iconGroupHeader, /*#__PURE__*/React__default.createElement(SideNavGroupContext.Provider, {
      value: {
        isInDropdownGroup: true
      }
    }, isGroupExpanded && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Card, {
      as: 'ul',
      className: "C-SideNav__List",
      onClick: function onClick() {
        return setIsGroupExpanded(false);
      },
      "data-testid": dataTestId
    }, (groupHeader.props.to || groupHeader.props.href) && groupHeader, groupContent, /*#__PURE__*/React__default.createElement("div", {
      "aria-label": "Closing menu",
      tabIndex: "0",
      onFocus: function onFocus() {
        return setIsGroupExpanded(false);
      }
    })), /*#__PURE__*/React__default.createElement(Overlay, {
      isVisible: true,
      onClick: function onClick() {
        return setIsGroupExpanded(false);
      }
    }))));
  } else {
    return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(As, _extends({
      className: finalClasses
    }, otherProps, {
      "data-testid": dataTestId
    }), groupHeader, /*#__PURE__*/React__default.createElement(Button, {
      iconOnly: true,
      "data-testid": dataTestId ? "".concat(dataTestId, "_groupToggle") : null,
      "aria-label": isGroupExpanded ? "Close this section" : "Open this section",
      "aria-expanded": isGroupExpanded,
      className: finalButtonClasses,
      size: "dense",
      onClick: toggleClick
    }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
      icon: ChevronDownIcon
    }))), /*#__PURE__*/React__default.createElement(SideNavGroupContext.Provider, {
      value: {
        isInDropdownGroup: false
      }
    }, isGroupExpanded && /*#__PURE__*/React__default.createElement("div", {
      className: finalContentClasses
    }, groupContent)));
  }
};
SideNavNavigationGroup.displayName = 'SideNav.NavigationGroup';

export { SideNavNavigationGroup as default };
